import { supabase } from '../lib/supabase';

/**
 * Dashboard service for Abidjan agents
 * Provides container and delivery data WITHOUT financial information
 */
export const agentDashboardService = {
  /**
   * Get containers for Abidjan warehouse
   * NO financial data (total_amount, etc.)
   */
  async getAbidjanContainers() {
    try {
      const { data, error } = await supabase?.from('containers')?.select(`
          id,
          container_code,
          vessel_name,
          shipping_line,
          departure_port,
          destination_port,
          departure_date,
          eta_date,
          status,
          total_packages,
          total_shipments,
          notes,
          created_at
        `)?.in('status', ['ARRIVE_A_ABIDJAN', 'DEDOUANEMENT_EN_COURS', 'TRANSFERT_A_ENTREPOT', 'DISTRIBUTION_EN_COURS'])?.order('eta_date', { ascending: true });

      if (error) throw error;

      return (data || [])?.map(container => ({
        id: container?.id,
        containerCode: container?.container_code,
        vesselName: container?.vessel_name,
        shippingLine: container?.shipping_line,
        departurePort: container?.departure_port,
        destinationPort: container?.destination_port,
        departureDate: container?.departure_date,
        etaDate: container?.eta_date,
        status: container?.status,
        totalPackages: container?.total_packages,
        totalShipments: container?.total_shipments,
        notes: container?.notes,
        createdAt: container?.created_at
      })) || [];
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get containers with delivery task statistics for Abidjan
   * Shows container reference, arrival date, status, and delivery counters
   * NO financial data included
   */
  async getAbidjanContainersWithDeliveryStats() {
    try {
      // Get all Abidjan containers with relevant statuses for agent work
      const { data: containers, error: containerError } = await supabase
        ?.from('containers')
        ?.select(`
          id,
          container_code,
          eta_date,
          status,
          created_at
        `)
        ?.eq('destination_port', 'Abidjan')
        ?.in('status', ['ARRIVE_A_ABIDJAN', 'DEDOUANEMENT_EN_COURS', 'TRANSFERT_A_ENTREPOT', 'DISTRIBUTION_EN_COURS'])
        ?.order('eta_date', { ascending: false });

      if (containerError) throw containerError;

      // Get delivery tasks for each container
      const containerIds = (containers || [])?.map(c => c?.id) || [];
      
      const { data: deliveryTasks, error: tasksError } = await supabase
        ?.from('delivery_tasks')
        ?.select('container_id, status')
        ?.in('container_id', containerIds);

      if (tasksError) throw tasksError;

      // Calculate delivery statistics for each container
      const containersWithStats = (containers || [])?.map(container => {
        const containerTasks = (deliveryTasks || [])?.filter(t => t?.container_id === container?.id) || [];
        const totalDeliveries = containerTasks?.length || 0;
        const deliveredCount = containerTasks?.filter(t => t?.status === 'LIVRE')?.length || 0;
        
        // Determine container display status
        let displayStatus = 'EN_ATTENTE';
        if (deliveredCount === totalDeliveries && totalDeliveries > 0) {
          displayStatus = 'TERMINE';
        } else if (deliveredCount > 0 && deliveredCount < totalDeliveries) {
          displayStatus = 'EN_DISTRIBUTION';
        }

        return {
          id: container?.id,
          containerCode: container?.container_code,
          etaDate: container?.eta_date,
          status: displayStatus,
          totalDeliveries: totalDeliveries,
          deliveredCount: deliveredCount,
          createdAt: container?.created_at
        };
      });

      return containersWithStats;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get deliveries to prepare for agents
   * NO financial data (prices, amounts, paid_amount, etc.)
   * INCLUDES sender information for delivery planning
   */
  async getDeliveriesToPrepare() {
    try {
      const { data, error } = await supabase?.from('shipments')?.select(`
          id,
          reference,
          current_status,
          sender_name,
          sender_phone,
          sender_address,
          sender_city,
          sender_country,
          receiver_name,
          receiver_phone,
          receiver_address,
          receiver_city,
          receiver_country,
          estimated_delivery_date,
          container_id,
          description,
          weight,
          dimensions,
          created_at
        `)?.in('current_status', ['customs', 'out_for_delivery'])?.order('estimated_delivery_date', { ascending: true });

      if (error) throw error;

      return data?.map(shipment => ({
        id: shipment?.id,
        reference: shipment?.reference,
        currentStatus: shipment?.current_status,
        senderName: shipment?.sender_name,
        senderPhone: shipment?.sender_phone,
        senderAddress: shipment?.sender_address,
        senderCity: shipment?.sender_city,
        senderCountry: shipment?.sender_country,
        receiverName: shipment?.receiver_name,
        receiverPhone: shipment?.receiver_phone,
        receiverAddress: shipment?.receiver_address,
        receiverCity: shipment?.receiver_city,
        receiverCountry: shipment?.receiver_country,
        estimatedDeliveryDate: shipment?.estimated_delivery_date,
        containerId: shipment?.container_id,
        description: shipment?.description,
        weight: shipment?.weight,
        dimensions: shipment?.dimensions,
        createdAt: shipment?.created_at
      }));
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get pickup tasks assigned to agent
   * NO financial data
   */
  async getAgentPickupTasks(agentEmployeeId) {
    try {
      const { data, error } = await supabase?.from('pickup_tasks')?.select(`
          id,
          shipment_id,
          date,
          time_window,
          status,
          client_name,
          client_phone,
          client_address,
          client_city,
          client_zone,
          notes,
          client_signature_url,
          pickup_photo_urls,
          created_at
        `)?.eq('driver_id', agentEmployeeId)?.order('date', { ascending: true });

      if (error) throw error;

      return data?.map(task => ({
        id: task?.id,
        shipmentId: task?.shipment_id,
        date: task?.date,
        timeWindow: task?.time_window,
        status: task?.status,
        clientName: task?.client_name,
        clientPhone: task?.client_phone,
        clientAddress: task?.client_address,
        clientCity: task?.client_city,
        clientZone: task?.client_zone,
        notes: task?.notes,
        clientSignatureUrl: task?.client_signature_url,
        pickupPhotoUrls: task?.pickup_photo_urls,
        createdAt: task?.created_at
      }));
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get shipment details for agent (NO FINANCIAL DATA)
   */
  async getShipmentDetails(shipmentId) {
    try {
      const { data, error } = await supabase?.from('shipments')?.select(`
          id,
          reference,
          current_status,
          receiver_name,
          receiver_phone,
          receiver_address,
          receiver_city,
          receiver_country,
          sender_name,
          sender_phone,
          sender_address,
          sender_city,
          sender_country,
          description,
          weight,
          dimensions,
          shipment_type,
          estimated_delivery_date,
          actual_delivery_date,
          container_id,
          photo_url,
          photos_urls,
          created_at,
          updated_at
        `)?.eq('id', shipmentId)?.single();

      if (error) throw error;

      return {
        id: data?.id,
        reference: data?.reference,
        currentStatus: data?.current_status,
        receiverName: data?.receiver_name,
        receiverPhone: data?.receiver_phone,
        receiverAddress: data?.receiver_address,
        receiverCity: data?.receiver_city,
        receiverCountry: data?.receiver_country,
        senderName: data?.sender_name,
        senderPhone: data?.sender_phone,
        senderAddress: data?.sender_address,
        senderCity: data?.sender_city,
        senderCountry: data?.sender_country,
        description: data?.description,
        weight: data?.weight,
        dimensions: data?.dimensions,
        shipmentType: data?.shipment_type,
        estimatedDeliveryDate: data?.estimated_delivery_date,
        actualDeliveryDate: data?.actual_delivery_date,
        containerId: data?.container_id,
        photoUrl: data?.photo_url,
        photosUrls: data?.photos_urls,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get delivery tasks assigned to agent (Abidjan deliveries)
   * Filters by assigned_employee_id and provides status counters
   * NOW INCLUDES: delivery_zone and route_order for route optimization
   */
  async getAgentDeliveryTasks(employeeId, statusFilter = null, dateFilter = null) {
    try {
      let query = supabase?.from('delivery_tasks')?.select(`
          id,
          shipment_id,
          container_id,
          assigned_employee_id,
          planned_date,
          planned_time_window,
          status,
          recipient_name,
          recipient_phone,
          delivery_address,
          delivery_notes,
          delivery_photo_urls,
          delivery_signature_url,
          delivered_at,
          delivery_zone,
          route_order,
          incident_type,
          incident_comment,
          created_at,
          updated_at
        `)?.eq('assigned_employee_id', employeeId);

      // Apply status filter if provided
      if (statusFilter && statusFilter !== 'ALL') {
        query = query?.eq('status', statusFilter);
      }

      // Apply date filter if provided
      if (dateFilter) {
        query = query?.eq('planned_date', dateFilter);
      }

      // Order by delivery_zone first, then route_order within each zone
      query = query
        ?.order('delivery_zone', { ascending: true, nullsFirst: false })
        ?.order('route_order', { ascending: true, nullsFirst: false })
        ?.order('planned_date', { ascending: true })
        ?.order('planned_time_window', { ascending: true });

      const { data, error } = await query;

      if (error) throw error;

      return data?.map(task => ({
        id: task?.id,
        shipmentId: task?.shipment_id,
        containerId: task?.container_id,
        assignedEmployeeId: task?.assigned_employee_id,
        plannedDate: task?.planned_date,
        plannedTimeWindow: task?.planned_time_window,
        status: task?.status,
        recipientName: task?.recipient_name,
        recipientPhone: task?.recipient_phone,
        deliveryAddress: task?.delivery_address,
        deliveryNotes: task?.delivery_notes,
        deliveryPhotoUrls: task?.delivery_photo_urls,
        deliverySignatureUrl: task?.delivery_signature_url,
        deliveredAt: task?.delivered_at,
        deliveryZone: task?.delivery_zone,
        routeOrder: task?.route_order,
        incidentType: task?.incident_type,
        incidentComment: task?.incident_comment,
        createdAt: task?.created_at,
        updatedAt: task?.updated_at
      }));
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get delivery task status counters for agent dashboard
   * Returns count for each status type
   */
  async getDeliveryTaskCounters(employeeId) {
    try {
      const { data, error } = await supabase?.from('delivery_tasks')?.select('status')?.eq('assigned_employee_id', employeeId);

      if (error) throw error;

      const counters = {
        A_PREPARER: 0,
        EN_LIVRAISON: 0,
        LIVRE: 0,
        INCIDENT: 0,
        PROBLEME: 0,
        total: 0
      };

      data?.forEach(task => {
        const status = task?.status;
        if (counters?.hasOwnProperty(status)) {
          counters[status]++;
        }
        counters.total++;
      });

      return counters;
    } catch (error) {
      console.error('Error fetching delivery task counters:', error);
      throw error;
    }
  },

  /**
   * Update delivery task status and optional fields
   * NOW INCLUDES: delivery_zone and route_order for route optimization
   */
  async updateDeliveryTaskStatus(taskId, updates) {
    try {
      const { data, error } = await supabase
        ?.from('delivery_tasks')
        ?.update({
          status: updates?.status,
          delivery_photo_urls: updates?.deliveryPhotoUrls,
          delivery_signature_url: updates?.deliverySignatureUrl,
          delivery_notes: updates?.deliveryNotes,
          incident_type: updates?.incidentType,
          incident_comment: updates?.incidentComment,
          delivery_zone: updates?.deliveryZone,
          route_order: updates?.routeOrder,
          delivered_at: updates?.status === 'LIVRE' ? new Date()?.toISOString() : undefined
        })
        ?.eq('id', taskId)
        ?.select()
        ?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating delivery task:', error);
      throw error;
    }
  },

  /**
   * Update route order for a delivery task (only allowed if status = A_PREPARER)
   */
  async updateRouteOrder(taskId, newRouteOrder) {
    try {
      // First check if task status is A_PREPARER
      const { data: task, error: checkError } = await supabase
        ?.from('delivery_tasks')
        ?.select('status')
        ?.eq('id', taskId)
        ?.single();

      if (checkError) throw checkError;

      if (task?.status !== 'A_PREPARER') {
        throw new Error('Impossible de réordonner: la livraison doit être au statut A_PREPARER');
      }

      const { data, error } = await supabase
        ?.from('delivery_tasks')
        ?.update({ route_order: newRouteOrder })
        ?.eq('id', taskId)
        ?.select()
        ?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating route order:', error);
      throw error;
    }
  },

  /**
   * Assign delivery zones and route order automatically when opening container
   * Groups deliveries by zone and assigns sequential route_order within each zone
   */
  async assignDeliveryZonesAndOrder(containerId) {
    try {
      // Get all delivery tasks for this container
      const { data: tasks, error: tasksError } = await supabase
        ?.from('delivery_tasks')
        ?.select('id, delivery_address, status')
        ?.eq('container_id', containerId)
        ?.order('delivery_address', { ascending: true });

      if (tasksError) throw tasksError;

      if (!tasks || tasks?.length === 0) {
        return {
          success: true,
          message: 'Aucune tâche de livraison à organiser',
          tasksUpdated: 0
        };
      }

      // Simple zone extraction from address (can be enhanced later)
      // Common Abidjan zones: Cocody, Yopougon, Plateau, Marcory, Treichville, Adjame, Abobo, Koumassi
      const zoneKeywords = ['cocody', 'yopougon', 'plateau', 'marcory', 'treichville', 'adjame', 'abobo', 'koumassi'];
      
      const tasksWithZones = tasks?.map(task => {
        const address = task?.delivery_address?.toLowerCase() || '';
        let zone = 'Autres'; // Default zone
        
        for (const keyword of zoneKeywords) {
          if (address?.includes(keyword)) {
            zone = keyword?.charAt(0)?.toUpperCase() + keyword?.slice(1);
            break;
          }
        }
        
        return {
          id: task?.id,
          zone: zone,
          status: task?.status
        };
      });

      // Group by zone and assign route_order
      const tasksByZone = {};
      tasksWithZones?.forEach(task => {
        if (!tasksByZone?.[task?.zone]) {
          tasksByZone[task?.zone] = [];
        }
        tasksByZone?.[task?.zone]?.push(task);
      });

      // Update each task with zone and route_order
      const updates = [];
      Object?.keys(tasksByZone)?.forEach(zone => {
        tasksByZone?.[zone]?.forEach((task, index) => {
          updates?.push(
            supabase
              ?.from('delivery_tasks')
              ?.update({
                delivery_zone: zone,
                route_order: index + 1
              })
              ?.eq('id', task?.id)
          );
        });
      });

      await Promise?.all(updates);

      return {
        success: true,
        message: `${tasks?.length} tâches organisées par zone`,
        tasksUpdated: tasks?.length,
        zonesSummary: Object?.keys(tasksByZone)?.map(zone => ({
          zone: zone,
          count: tasksByZone?.[zone]?.length
        }))
      };
    } catch (error) {
      console.error('Error assigning delivery zones and order:', error);
      throw error;
    }
  },

  /**
   * Open Abidjan container - list shipments and create delivery tasks
   * Creates delivery_task for each shipment if not already exists
   * Prevents duplicates (1 delivery_task per shipment)
   */
  async openAbidjanContainer(containerId, assignedEmployeeId) {
    try {
      // 1. Get all shipments for this container
      const { data: shipments, error: shipmentsError } = await supabase
        ?.from('shipments')
        ?.select(`
          id,
          reference,
          receiver_name,
          receiver_phone,
          receiver_address,
          receiver_city,
          estimated_delivery_date,
          current_status,
          description
        `)
        ?.eq('container_id', containerId)
        ?.order('reference', { ascending: true });

      if (shipmentsError) throw shipmentsError;

      if (!shipments || shipments?.length === 0) {
        return {
          success: true,
          message: 'Aucun shipment dans ce conteneur',
          shipments: [],
          tasksCreated: 0,
          tasksAlreadyExist: 0
        };
      }

      // 2. Get existing delivery_tasks for these shipments
      const shipmentIds = shipments?.map(s => s?.id);
      
      const { data: existingTasks, error: tasksError } = await supabase
        ?.from('delivery_tasks')
        ?.select('shipment_id')
        ?.in('shipment_id', shipmentIds);

      if (tasksError) throw tasksError;

      // 3. Identify shipments WITHOUT delivery_tasks
      const existingShipmentIds = new Set(existingTasks?.map(t => t?.shipment_id));
      const shipmentsNeedingTasks = shipments?.filter(s => !existingShipmentIds?.has(s?.id));

      // 4. Create delivery_tasks for shipments without tasks
      let tasksCreated = 0;
      const createdTasks = [];

      if (shipmentsNeedingTasks?.length > 0) {
        const tasksToCreate = shipmentsNeedingTasks?.map(shipment => ({
          container_id: containerId,
          shipment_id: shipment?.id,
          assigned_employee_id: assignedEmployeeId,
          status: 'A_PREPARER',
          recipient_name: shipment?.receiver_name,
          recipient_phone: shipment?.receiver_phone,
          delivery_address: `${shipment?.receiver_address}, ${shipment?.receiver_city}`,
          planned_date: shipment?.estimated_delivery_date || new Date()?.toISOString()?.split('T')?.[0],
          planned_time_window: '09h-18h',
          delivery_notes: shipment?.description || null
        }));

        const { data: newTasks, error: createError } = await supabase
          ?.from('delivery_tasks')
          ?.insert(tasksToCreate)
          ?.select();

        if (createError) throw createError;

        tasksCreated = newTasks?.length;
        createdTasks?.push(...newTasks);
      }

      return {
        success: true,
        message: `${tasksCreated} tâche(s) créée(s), ${existingTasks?.length} déjà existante(s)`,
        shipments: shipments?.map(s => ({
          id: s?.id,
          reference: s?.reference,
          receiverName: s?.receiver_name,
          receiverPhone: s?.receiver_phone,
          receiverAddress: s?.receiver_address,
          receiverCity: s?.receiver_city,
          estimatedDeliveryDate: s?.estimated_delivery_date,
          currentStatus: s?.current_status,
          hasDeliveryTask: existingShipmentIds?.has(s?.id)
        })),
        tasksCreated: tasksCreated,
        tasksAlreadyExist: existingTasks?.length,
        createdTasks: createdTasks?.map(t => ({
          id: t?.id,
          shipmentId: t?.shipment_id,
          status: t?.status,
          plannedDate: t?.planned_date,
          recipientName: t?.recipient_name
        }))
      };
    } catch (error) {
      console.error('Error opening Abidjan container:', error);
      throw error;
    }
  },

  /**
   * Get shipments for a specific container (without creating tasks)
   * Used for viewing/verification before opening container
   * INCLUDES sender information (name, phone, address, city) for complete delivery context
   */
  async getContainerShipments(containerId) {
    try {
      const { data: shipments, error } = await supabase
        ?.from('shipments')
        ?.select(`
          id,
          reference,
          sender_name,
          sender_phone,
          sender_address,
          sender_city,
          sender_country,
          receiver_name,
          receiver_phone,
          receiver_address,
          receiver_city,
          receiver_country,
          delivery_commune,
          delivery_quartier,
          estimated_delivery_date,
          current_status,
          description,
          weight,
          dimensions
        `)
        ?.eq('container_id', containerId)
        ?.order('reference', { ascending: true });

      if (error) throw error;

      // Check which shipments have delivery tasks
      if (shipments && shipments?.length > 0) {
        const shipmentIds = shipments?.map(s => s?.id);
        
        const { data: deliveryTasks, error: tasksError } = await supabase
          ?.from('delivery_tasks')
          ?.select('shipment_id, status')
          ?.in('shipment_id', shipmentIds);

        if (tasksError) throw tasksError;

        const tasksByShipment = {};
        deliveryTasks?.forEach(task => {
          tasksByShipment[task?.shipment_id] = task?.status;
        });

        return shipments?.map(shipment => ({
          id: shipment?.id,
          reference: shipment?.reference,
          senderName: shipment?.sender_name,
          senderPhone: shipment?.sender_phone,
          senderAddress: shipment?.sender_address,
          senderCity: shipment?.sender_city,
          senderCountry: shipment?.sender_country,
          receiverName: shipment?.receiver_name,
          receiverPhone: shipment?.receiver_phone,
          receiverAddress: shipment?.receiver_address,
          receiverCity: shipment?.receiver_city,
          receiverCountry: shipment?.receiver_country,
          deliveryCommune: shipment?.delivery_commune,
          deliveryQuartier: shipment?.delivery_quartier,
          estimatedDeliveryDate: shipment?.estimated_delivery_date,
          currentStatus: shipment?.current_status,
          description: shipment?.description,
          weight: shipment?.weight,
          dimensions: shipment?.dimensions,
          hasDeliveryTask: !!tasksByShipment?.[shipment?.id],
          deliveryTaskStatus: tasksByShipment?.[shipment?.id] || null
        }));
      }

      return [];
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get container shipments grouped by commune and quartier (Abidjan automatic grouping)
   * Groups deliveries for organized agent view: Commune → Quartier → delivery_tasks
   * Includes counters per quartier (total/LIVRE/INCIDENT/remaining)
   * Implements sorting: Commune A-Z, Quartier A-Z, then by status
   */
  async getContainerShipmentsGroupedByCommuneQuartier(containerId) {
    try {
      // Fetch shipments with delivery_commune and delivery_quartier
      const { data: shipments, error: shipmentsError } = await supabase
        ?.from('shipments')
        ?.select(`
          id,
          reference,
          sender_name,
          sender_city,
          receiver_name,
          receiver_phone,
          receiver_address,
          delivery_commune,
          delivery_quartier,
          description
        `)
        ?.eq('container_id', containerId)
        ?.order('delivery_commune', { ascending: true, nullsFirst: false })
        ?.order('delivery_quartier', { ascending: true, nullsFirst: false });

      if (shipmentsError) throw shipmentsError;

      if (!shipments || shipments?.length === 0) {
        return { grouped: {}, totalShipments: 0 };
      }

      // Get delivery tasks for these shipments
      const shipmentIds = shipments?.map(s => s?.id);
      
      const { data: deliveryTasks, error: tasksError } = await supabase
        ?.from('delivery_tasks')
        ?.select('shipment_id, status')
        ?.in('shipment_id', shipmentIds);

      if (tasksError) throw tasksError;

      // Map tasks by shipment
      const tasksByShipment = {};
      deliveryTasks?.forEach(task => {
        tasksByShipment[task?.shipment_id] = task?.status;
      });

      // Group by commune, then by quartier
      const grouped = {};
      
      shipments?.forEach(shipment => {
        // Fallback logic for empty values
        const commune = shipment?.delivery_commune || 'Commune non renseignée';
        const quartier = shipment?.delivery_quartier || 'Quartier non renseigné';
        const taskStatus = tasksByShipment?.[shipment?.id] || null;

        // Initialize commune if not exists
        if (!grouped?.[commune]) {
          grouped[commune] = {};
        }

        // Initialize quartier if not exists
        if (!grouped?.[commune]?.[quartier]) {
          grouped[commune][quartier] = {
            shipments: [],
            counters: {
              total: 0,
              LIVRE: 0,
              INCIDENT: 0,
              remaining: 0
            }
          };
        }

        // Add shipment to quartier
        grouped?.[commune]?.[quartier]?.shipments?.push({
          id: shipment?.id,
          reference: shipment?.reference,
          senderName: shipment?.sender_name,
          senderCity: shipment?.sender_city,
          receiverName: shipment?.receiver_name,
          receiverPhone: shipment?.receiver_phone,
          receiverAddress: shipment?.receiver_address,
          deliveryCommune: commune,
          deliveryQuartier: quartier,
          description: shipment?.description,
          hasDeliveryTask: !!taskStatus,
          deliveryTaskStatus: taskStatus
        });

        // Update counters
        grouped[commune][quartier].counters.total++;
        
        if (taskStatus === 'LIVRE') {
          grouped[commune][quartier].counters.LIVRE++;
        } else if (taskStatus === 'INCIDENT') {
          grouped[commune][quartier].counters.INCIDENT++;
        } else if (taskStatus) {
          // Count A_PREPARER, EN_LIVRAISON as remaining
          grouped[commune][quartier].counters.remaining++;
        }
      });

      // Sort shipments within each quartier by status priority
      const statusPriority = { 'A_PREPARER': 1, 'EN_LIVRAISON': 2, 'INCIDENT': 3, 'LIVRE': 4 };
      
      Object?.keys(grouped)?.forEach(commune => {
        Object?.keys(grouped?.[commune])?.forEach(quartier => {
          grouped?.[commune]?.[quartier]?.shipments?.sort((a, b) => {
            const priorityA = statusPriority?.[a?.deliveryTaskStatus] || 5;
            const priorityB = statusPriority?.[b?.deliveryTaskStatus] || 5;
            return priorityA - priorityB;
          });
        });
      });

      return {
        grouped,
        totalShipments: shipments?.length
      };
    } catch (error) {
      console.error('Error fetching grouped shipments:', error);
      throw error;
    }
  }
};